import { findLastIndex, reverse } from 'ramda';
import { FlushVolumeMode } from 'providers/api';
import { crossSectionalArea } from '../Route/volumeUtils';
import PCSpool from '../Spools/PCSpool';

export interface LineSegmentGeometry {
  length: number;
  area: number;
}

// Cumulative distance and internal volume, measured from the plant.
export interface VolumeBreakpoint {
  distance: number;
  volume: number;
}

// Piecewise-linear map between "metres of line filled from the plant" and "water pumped". Line
// volume is not proportional to length because routes cross several pipe sizes, so both directions
// of the map go through this table.
export interface VolumeTable {
  breakpoints: VolumeBreakpoint[];
  totalLength: number;
  totalVolume: number;
}

// Segments in plant -> stope order.
export const buildVolumeTable = (segments: LineSegmentGeometry[]): VolumeTable => {
  const breakpoints = segments.reduce<VolumeBreakpoint[]>((acc, { length, area }) => {
    const last = acc[acc.length - 1];
    return [...acc, { distance: last.distance + length, volume: last.volume + (area * length) }];
  }, [{ distance: 0, volume: 0 }]);
  const last = breakpoints[breakpoints.length - 1];
  return { breakpoints, totalLength: last.distance, totalVolume: last.volume };
};

// Route spools run stope -> plant; the flush coordinate runs the other way. Choke loops are real
// pipe holding real water, so spool.length() (which includes them) is the right length.
export const segmentsPlantToStope = (spoolsStopeToPlant: PCSpool[]): LineSegmentGeometry[] => reverse(spoolsStopeToPlant)
  .map((spool) => ({ length: spool.length(), area: crossSectionalArea(spool.pipe?.insideDiameter ?? 0) }));

// Metres down the line the water front has reached after `volume` m3 of water. Clamped to the line.
// A segment with no pipe size holds no volume, so the front passes through it for free.
export const frontFromVolume = (table: VolumeTable, volume: number): number => {
  const { breakpoints, totalLength, totalVolume } = table;
  if (volume <= 0) return 0;
  if (volume >= totalVolume) return totalLength;

  const index = findLastIndex((bp) => bp.volume <= volume, breakpoints);
  const from = breakpoints[index];
  const to = breakpoints[index + 1];
  if (!to) return from.distance;

  const segmentVolume = to.volume - from.volume;
  if (segmentVolume <= 0) return to.distance;
  return from.distance + ((to.distance - from.distance) * ((volume - from.volume) / segmentVolume));
};

// Water needed to bring the front to `distance` metres down the line. Exact inverse of frontFromVolume
// wherever the line has volume.
export const volumeFromFront = (table: VolumeTable, distance: number): number => {
  const { breakpoints, totalLength, totalVolume } = table;
  if (distance <= 0) return 0;
  if (distance >= totalLength) return totalVolume;

  const index = findLastIndex((bp) => bp.distance <= distance, breakpoints);
  const from = breakpoints[index];
  const to = breakpoints[index + 1];
  if (!to) return from.volume;

  const segmentLength = to.distance - from.distance;
  if (segmentLength <= 0) return to.volume;
  return from.volume + ((to.volume - from.volume) * ((distance - from.distance) / segmentLength));
};

const assertNever = (mode: never): never => {
  throw new Error(`Unhandled flush volume mode ${String(mode)}`);
};

// The planned water volume for a mode. AirStartVolume resolves to the air-start volume the caller
// has already found (undefined when air cannot start within the line).
export const resolveFinalFlushVolume = (
  mode: FlushVolumeMode,
  value: number,
  table: VolumeTable,
  airStartVolume?: number,
): number | undefined => {
  switch (mode) {
    case FlushVolumeMode.Explicit:
      return Math.max(value, 0);
    case FlushVolumeMode.PipeVolumeMultiple:
      return Math.max(value, 0) * table.totalVolume;
    case FlushVolumeMode.PipeVolumePlus:
      return table.totalVolume + Math.max(value, 0);
    case FlushVolumeMode.Distance:
      return volumeFromFront(table, value);
    case FlushVolumeMode.AirStartVolume:
      return airStartVolume;
    default:
      return assertNever(mode);
  }
};
